<template>
    <div>
        <!-- Full-screen loading overlay -->
        <v-overlay v-model="pageLoading" class="align-center justify-center" persistent>
            <div class="text-center">
                <v-progress-circular
                    indeterminate
                    size="64"
                    color="primary"
                ></v-progress-circular>
                <div class="mt-4 text-h6">Đang tải dữ liệu...</div>
            </div>
        </v-overlay>

        <v-card class="mb-6">
            <v-card-title class="text-h6">
                <v-icon class="mr-2">mdi-pencil-box</v-icon>
                Cập nhật sản phẩm
            </v-card-title>
            <v-card-text>
                <v-form @submit.prevent="updateProduct" ref="form" v-model="valid">
                    <v-row>
                        <v-col cols="12" md="6">
                            <v-text-field v-model="form.name" label="Tên sản phẩm" :rules="[v => !!v || 'Bắt buộc']" required />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field v-model.number="form.price" label="Giá (VNĐ)" type="number" :rules="[v => v >= 0 || 'Không âm']" required />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field v-model.number="form.sale_off" label="Giảm giá (VNĐ)" type="number" :rules="[v => v >= 0 || 'Không âm']" required />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field v-model.number="form.affiliate_share" label="Affiliate Share (%)" type="number" :rules="[v => v >= 0 || 'Không âm']" required />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-select v-model="form.category_id" :items="categories" item-title="name" item-value="id" label="Danh mục" required />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-checkbox
                                v-model="form.is_license"
                                label="Sản phẩm có License (Key)"
                                color="primary"
                                hide-details
                                :true-value="1"
                                :false-value="0"
                            ></v-checkbox>
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-autocomplete
                                v-model="form.tags"
                                :items="availableTags"
                                label="Tags"
                                placeholder="Tìm kiếm và chọn tags hoặc nhập tag mới..."
                                variant="outlined"
                                density="compact"
                                multiple
                                chips
                                closable-chips
                                clearable
                                :search-input.sync="tagSearch"
                                @update:search="onTagSearch"
                                @update:model-value="onTagsChange"
                                @keydown.enter="addNewTag(tagSearch)"
                                :loading="loadingTags"
                                :filter="() => true"
                                item-title="name"
                                item-value="name"
                                hint="Chọn tags từ danh sách có sẵn hoặc nhập tag mới và nhấn Enter"
                                persistent-hint
                                allow-input
                            >
                                <template v-slot:chip="{ props, item }">
                                    <v-chip
                                        v-bind="props"
                                        :text="item.raw.name"
                                        color="primary"
                                        variant="outlined"
                                        size="small"
                                    />
                                </template>
                                <template v-slot:item="{ props, item }">
                                    <v-list-item v-bind="props">
                                        <template v-slot:prepend>
                                            <v-icon icon="mdi-tag" color="primary" size="small" />
                                        </template>
                                    </v-list-item>
                                </template>
                            </v-autocomplete>
                        </v-col>
                        <v-col cols="12" md="6">
                            <label class="mb-2 font-weight-medium">Thumbnail</label>
                            <file-input
                                v-model="form.thumbnail"
                                accept="image/*"
                                title="Tải ảnh thumbnail"
                                subtitle="Chọn ảnh đại diện cho sản phẩm"
                                :max-size="5 * 1024 * 1024"
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <label class="mb-2 font-weight-medium">Mô tả sản phẩm</label>
                            <html-editor
                                v-model="form.description"
                                height="300px"
                                placeholder="Nhập mô tả sản phẩm (HTML)..."
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <label class="mb-2 font-weight-medium">Template Email</label>
                            <html-editor
                                v-model="form.template_email"
                                height="300px"
                                placeholder="Nhập template email (HTML)..."
                            />
                        </v-col>
                        <v-col cols="12">
                            <v-text-field
                                v-model="form.library_link"
                                label="Link thư viện Apps Script"
                                placeholder="https://script.google.com/d/..."
                                variant="outlined"
                                density="compact"
                                clearable
                                hint="Để trống nếu sản phẩm không có thư viện. Khách mua xong sẽ được tự cấp quyền xem."
                                persistent-hint
                            />
                        </v-col>
                        <v-col cols="12">
                            <v-text-field
                                v-model="form.license_sheet_link"
                                label="Link danh sách bản quyền (Google Sheet)"
                                placeholder="https://docs.google.com/spreadsheets/d/..."
                                variant="outlined"
                                density="compact"
                                clearable
                                hint="Để trống nếu không dùng. Khách mua xong sẽ được thêm email vào một dòng mới trong sheet này."
                                persistent-hint
                            />
                        </v-col>
                        <v-col cols="12">
                            <label class="mb-2 font-weight-medium">Landing Page (File ZIP)</label>
                            <file-input
                                v-model="form.landing_page_file"
                                accept=".zip"
                                title="Tải file ZIP Landing Page"
                                subtitle="Chọn file ZIP chứa trang landing page"
                                :max-size="50 * 1024 * 1024"
                            />
                        </v-col>
                        <v-col cols="12">
                            <label class="mb-2 font-weight-medium">Slide Ảnh</label>
                            <div class="slide-images-container">
                                <div v-for="(img, idx) in form.slide" :key="idx" class="slide-image-item">
                                    <file-input
                                        v-model="form.slide[idx]"
                                        accept="image/*"
                                        :title="`Slide ${idx + 1}`"
                                        subtitle="Chọn ảnh cho slide"
                                        :max-size="5 * 1024 * 1024"
                                    />
                                    <v-btn 
                                        icon 
                                        color="error" 
                                        @click="removeSlideImg(idx)" 
                                        v-if="form.slide.length > 1"
                                        class="remove-slide-btn"
                                    >
                                        <v-icon>mdi-minus</v-icon>
                                    </v-btn>
                                </div>
                                <div class="add-slide-container">
                                    <v-btn color="primary" @click="addSlideImg" outlined>
                                        <v-icon left>mdi-plus</v-icon>
                                        Thêm slide
                                    </v-btn>
                                </div>
                            </div>
                        </v-col>
                        <v-col cols="12" class="d-flex justify-space-between">
                            <v-btn color="primary" type="submit" :loading="loading" :disabled="!valid">
                                <v-icon class="mr-2">mdi-content-save</v-icon>
                                Cập nhật sản phẩm
                            </v-btn>
                            <v-btn color="secondary" @click="$router.push('/admin/product')" outlined>
                                <v-icon class="mr-2">mdi-arrow-left</v-icon>
                                Quay lại
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-form>
            </v-card-text>
        </v-card>

        <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000" top right>
            {{ snackbarText }}
        </v-snackbar>
    </div>
</template>

<script>
import HtmlEditor from '@/components/HtmlEditor.vue';
import FileInput from '@/components/FileInput.vue';
import axios from '@/http';
import { BASE_STORAGE } from '@/config';
export default {
    name: 'UpdateProduct',
    components: { HtmlEditor, FileInput },
    data() {
        return {
            valid: false,
            loading: false,
            pageLoading: false, // Full-screen loading state
            categories: [],
            snackbar: false,
            snackbarText: '',
            snackbarColor: 'success',
            form: {
                name: '',
                description: '',
                thumbnail: '',
                slide: [''],
                price: 0,
                sale_off: 0,
                template_email: '',
                affiliate_share: 0,
                category_id: null,
                landing_page_file: null,
                library_link: '',
                license_sheet_link: '',
                tags: [],
                is_license: 0
            },
            availableTags: [], // Available tags for autocomplete
            loadingTags: false, // Loading state for tags
            tagSearch: '', // Search input for tags
        }
    },
    methods: {
        addSlideImg() {
            this.form.slide.push('');
        },
        removeSlideImg(idx) {
            if (this.form.slide.length > 1) this.form.slide.splice(idx, 1);
        },
        async updateProduct() {
            if (!this.$refs.form.validate()) return;
            this.loading = true;
            const id = this.$route.params.id;
            
            // Chuẩn bị payload và xử lý dữ liệu từ FileInput components
            const payload = { ...this.form };
            
            // Xử lý slide images - gửi dưới dạng mảng thay vì string
            const validSlides = this.form.slide.filter(img => img && img.trim() !== '');
            if (validSlides.length > 0) {
                payload.slide = validSlides; // Gửi mảng thay vì string
            } else {
                payload.slide = [];
            }
            // Không gửi slide_img nữa
            
            // Xử lý tags (chuyển array thành string)
            if (Array.isArray(this.form.tags)) {
                payload.tags = this.form.tags.join(',');
            }
            
            // Xử lý landing page file (riêng biệt với content)
            if (this.form.landing_page_file && this.form.landing_page_file.data) {
                payload.landing_page_file = this.form.landing_page_file.data;
            }
            delete payload.landing_page_file;
            
            try {
                await axios.put(`/products/${id}`, payload);
                this.snackbarText = 'Cập nhật sản phẩm thành công!';
                this.snackbarColor = 'success';
                this.snackbar = true;
                setTimeout(() => {
                    this.$router.push('/admin/product');
                }, 1000);
            } catch (e) {
                console.error('Error updating product:', e);
                this.snackbarText = 'Cập nhật sản phẩm thất bại!';
                this.snackbarColor = 'error';
                this.snackbar = true;
            }
            this.loading = false;
        },
        async fetchProduct() {
            const id = this.$route.params.id;
            try {
                const res = await axios.get(`/products/${id}`);
                const data = res.data;
                
                // Xử lý slide_img từ chuỗi thành mảng
                let slideArr = [];
                if (typeof data.slide_img === 'string' && data.slide_img.trim()) {
                    slideArr = data.slide_img.split(',').map(img => img.trim()).filter(Boolean);
                }
                slideArr = slideArr.map(img => typeof img === 'string' && !img.startsWith('data:') ? BASE_STORAGE + img : img);
                
                // Xử lý thumbnail
                const thumbnailUrl = typeof data.thumbnail === 'string' && data.thumbnail && !data.thumbnail.startsWith('data:') ? BASE_STORAGE + data.thumbnail : data.thumbnail;
                
                // Xử lý tags từ chuỗi thành mảng
                let tagsArr = [];
                if (typeof data.tags === 'string' && data.tags.trim()) {
                    tagsArr = data.tags.split(',').map(tag => tag.trim()).filter(Boolean);
                }
                
                this.form = {
                    name: data.name || '',
                    description: data.description || '',
                    template_email: data.template_email || '',
                    price: data.price || 0,
                    sale_off: data.sale_off || 0,
                    affiliate_share: data.affiliate_share || 0,
                    category_id: data.category_id || null,
                    thumbnail: thumbnailUrl || '',
                    slide: slideArr.length ? slideArr : [''],
                    landing_page_file: null, // Reset for new upload, riêng với landing_page content
                    library_link: data.library_link || '',
                    license_sheet_link: data.license_sheet_link || '',
                    tags: tagsArr,
                    is_license: data.is_license || 0
                };
            } catch (e) {
                console.error('Error fetching product:', e);
                this.snackbarText = 'Lỗi khi tải dữ liệu sản phẩm!';
                this.snackbarColor = 'error';
                this.snackbar = true;
            }
        },
        async fetchCategories() {
            try {
                const response = await axios.get('/categories');
                this.categories = response.data;
            } catch (error) {
                console.error('Error fetching categories:', error);
            }
        },

        async searchTags(search = '', page = 1, limit = 20) {
            this.loadingTags = true;
            try {
                const response = await axios.get('/products/tags/simple', {
                    params: { search, page, limit }
                });
                
                if (response.data && response.data.success) {
                    // Response có dạng: { success: true, data: ["tag1", "tag2"], total: 2, search: "..." }
                    const tags = response.data.data || [];
                    // Chuyển đổi từ array string thành array object để phù hợp với v-autocomplete
                    this.availableTags = tags.map(tag => ({
                        name: tag,
                        count: 0 // Không có count trong response này
                    }));
                }
            } catch (error) {
                console.error('Error searching tags:', error);
                this.availableTags = [];
            } finally {
                this.loadingTags = false;
            }
        },

        onTagSearch(search) {
            this.tagSearch = search;
            if (search && search.length >= 2) {
                // Debounce search
                clearTimeout(this.tagSearchTimeout);
                this.tagSearchTimeout = setTimeout(() => {
                    this.searchTags(search);
                }, 300);
            } else if (!search) {
                // Load initial tags when search is cleared
                this.searchTags();
            }
        },

        addNewTag(tagName) {
            if (tagName && tagName.trim()) {
                const trimmedTag = tagName.trim();
                // Kiểm tra xem tag đã tồn tại chưa
                const existingTag = this.availableTags.find(t => t.name === trimmedTag);
                if (!existingTag) {
                    // Thêm tag mới vào availableTags
                    this.availableTags.push({
                        name: trimmedTag,
                        count: 0
                    });
                }
                // Thêm tag vào form.tags nếu chưa có
                if (!this.form.tags.includes(trimmedTag)) {
                    this.form.tags.push(trimmedTag);
                }
            }
        },

        onTagsChange(newTags) {
            // Xử lý khi tags thay đổi
            if (Array.isArray(newTags)) {
                // Kiểm tra xem có tag mới nào không
                newTags.forEach(tag => {
                    if (typeof tag === 'string' && tag.trim()) {
                        // Nếu là tag mới (không có trong availableTags)
                        const existingTag = this.availableTags.find(t => t.name === tag);
                        if (!existingTag) {
                            // Thêm tag mới vào availableTags
                            this.availableTags.push({
                                name: tag.trim(),
                                count: 0
                            });
                        }
                    }
                });
            }
        }
    },
    async mounted() {
        this.pageLoading = true;
        try {
            await Promise.all([
                this.fetchProduct(),
                this.fetchCategories(),
                this.searchTags() // Load tags ban đầu
            ]);
        } finally {
            this.pageLoading = false;
        }
    }
}
</script>

<style scoped>
.font-weight-medium {
    font-weight: 500;
}

.slide-images-container {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.slide-image-item {
    position: relative;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 12px;
    background-color: #fafafa;
}

.remove-slide-btn {
    position: absolute;
    top: 8px;
    right: 8px;
    background-color: white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    z-index: 10;
}

.add-slide-container {
    display: flex;
    justify-content: center;
    padding: 16px;
    border: 2px dashed #ddd;
    border-radius: 8px;
    background-color: #fafafa;
}

.add-slide-container:hover {
    border-color: #1976d2;
    background-color: #f3f8ff;
}

@media (max-width: 768px) {
    .slide-images-container {
        gap: 12px;
    }
    
    .slide-image-item {
        padding: 8px;
    }
}
</style>